import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Download, Book, FileText, Folder, DollarSign, Briefcase, Sparkles, Baby, Play, ShoppingCart, Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import TierBadge from '../components/TierBadge';
import AudioPlayer from '../components/AudioPlayer';
import logger from '../utils/logger';
import SponsoredBookCard from '../components/ads/SponsoredBookCard';
import BookBuyButton from '../components/BookBuyButton';

const API = process.env.REACT_APP_BACKEND_URL;

const DownloadsPage = () => {
  const [downloads, setDownloads] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedBookCategory, setSelectedBookCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [bookAccess, setBookAccess] = useState({}); // Track access for each book
  const [playingAudio, setPlayingAudio] = useState(null); // Currently playing audiobook
  const { user } = useAuth();

  const categories = [
    { id: 'all', label: 'All Items', icon: Folder },
    { id: 'books', label: 'Books', icon: Book },
    { id: 'materials', label: 'Materials', icon: FileText },
    { id: 'guides', label: 'Guides', icon: FileText },
    { id: 'other', label: 'Other', icon: Folder },
  ];

  const bookCategories = [
    { id: 'all', label: 'All Books', icon: Book },
    { id: 'finances', label: 'Finances', icon: DollarSign },
    { id: 'business', label: 'Business', icon: Briefcase },
    { id: 'spiritual', label: 'Spiritual', icon: Sparkles },
    { id: 'childrens_books', label: "Children's Books", icon: Baby },
  ];

  useEffect(() => {
    fetchDownloads();
  }, [selectedCategory, selectedBookCategory]);

  useEffect(() => {
    if (user && downloads.length > 0) {
      checkBookAccess();
    }
  }, [user, downloads]);

  const fetchDownloads = async () => {
    try {
      const params = {};
      if (selectedCategory !== 'all') {
        params.category = selectedCategory;
      }
      if (selectedBookCategory !== 'all' && selectedCategory === 'books') {
        params.book_category = selectedBookCategory;
      }
      const { data } = await axios.get(`${API}/api/downloads`, { params });
      setDownloads(data);
    } catch (error) {
      logger.error('Failed to load downloads:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkBookAccess = async () => {
    if (!user) return;
    
    const token = localStorage.getItem('access_token');
    const accessMap = {};
    
    for (const book of downloads) {
      try {
        const { data } = await axios.get(`${API}/api/downloads/${book.id}/access`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        accessMap[book.id] = data;
      } catch (error) {
        accessMap[book.id] = { purchased: false, can_download_pdf: false, can_access_audio: false };
      }
    }
    
    setBookAccess(accessMap);
  };

  const handlePurchase = async (book) => {
    if (!user) {
      window.location.href = '/login?redirect=/downloads';
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const origin = window.location.origin;

      const { data } = await axios.post(
        `${API}/api/downloads/${book.id}/purchase`,
        { origin_url: origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (data.url) {
        window.location.href = data.url; // Redirect to Stripe checkout
      }
    } catch (error) {
      logger.error('Purchase error:', error);
      alert(error.response?.data?.detail || 'Failed to initiate purchase');
    }
  };

  const handleDownloadPDF = async (book) => {
    const token = localStorage.getItem('access_token');
    
    try {
      // Download watermarked PDF from secure endpoint
      const response = await fetch(`${API}/api/downloads/${book.id}/download`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Download failed');
      }

      // Get the PDF blob
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${book.title.replace(/[^a-z0-9]/gi, '_')}_YOUNGBLACKMANINC.pdf`;
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      logger.error('Download error:', error);
      alert(error.message || 'Failed to download PDF');
    }
  };

  const handlePlayAudio = async (book) => {
    if (!user) {
      alert('Please log in to access audiobooks');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const { data } = await axios.get(`${API}/api/downloads/${book.id}/audio`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setPlayingAudio({
        bookId: book.id,
        title: book.title,
        audioUrl: data.audio_url,
        fullText: data.full_text || '',
        duration: data.duration_estimate
      });
    } catch (error) {
      logger.error('Audio error:', error);
      alert(error.response?.data?.detail || 'Failed to load audiobook');
    }
  };

  return (
    <div className="min-h-screen bg-black text-white pt-20">
      {/* Header */}
      <div className="relative h-[40vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A2216] via-black to-black opacity-90" />
        <div className="relative z-10 text-center px-6">
          {user?.subscription_tier && (
            <div className="mb-4">
              <TierBadge tier={user.subscription_tier} />
            </div>
          )}
          <h1 className="font-['Bebas_Neue'] text-5xl md:text-7xl mb-4">
            PDF BOOKS & MORE
          </h1>
          <p className="text-lg md:text-xl text-[#A3A3A3] max-w-2xl mx-auto">
            Credit repair, business tips, spiritual content & informational pamphlets - $2 each
          </p>
        </div>
      </div>

      {/* Category Filter */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <div className="flex flex-wrap gap-4 justify-center mb-8">
          {categories.map((cat) => {
            const Icon = cat.icon;
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`flex items-center gap-2 px-6 py-3 transition-all ${
                  selectedCategory === cat.id
                    ? 'bg-[#D4AF37] text-black'
                    : 'bg-white/5 text-white hover:bg-white/10'
                }`}
              >
                <Icon size={18} />
                {cat.label}
              </button>
            );
          })}
        </div>

        {/* Book Category Filter */}
        {selectedCategory === 'books' && (
          <div className="flex flex-wrap gap-3 justify-center mb-12 border-t border-white/10 pt-6">
            <span className="text-sm text-[#A3A3A3] self-center mr-2">Book Categories:</span>
            {bookCategories.map((cat) => {
              const Icon = cat.icon;
              return (
                <button
                  key={cat.id}
                  onClick={() => setSelectedBookCategory(cat.id)}
                  className={`flex items-center gap-2 px-4 py-2 text-sm transition-all ${
                    selectedBookCategory === cat.id
                      ? 'bg-[#D4AF37] text-black'
                      : 'bg-white/5 text-white hover:bg-white/10'
                  }`}
                >
                  <Icon size={16} />
                  {cat.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Downloads Grid */}
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : downloads.length === 0 ? (
          <div className="text-center py-20">
            <FileText size={64} className="mx-auto text-[#A3A3A3] mb-4" />
            <p className="text-xl text-[#A3A3A3]">No downloads available in this category</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <SponsoredBookCard />
            {downloads.map((download) => {
              const access = bookAccess[download.id] || {};
              const isPurchased = access.purchased;
              const canDownloadPDF = access.can_download_pdf;
              const canPlayAudio = access.can_access_audio;
              const isFree = download.price === 0;

              return (
                <div
                  key={download.id}
                  className="bg-[#1A1A1A] overflow-hidden group hover:bg-[#262626] transition-all"
                >
                  {/* Cover Image */}
                  <div className="aspect-[3/4] bg-gradient-to-br from-[#0A2216] to-[#0D0D0D] relative overflow-hidden">
                    {download.cover_image_url ? (
                      <img
                        src={download.cover_image_url}
                        alt={download.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <Book size={80} className="text-[#D4AF37]" />
                      </div>
                    )}
                    
                    {/* Price Badge */}
                    {!isFree && (
                      <div className="absolute top-4 right-4 bg-black/80 px-3 py-1 rounded-full">
                        <span className="text-[#D4AF37] font-bold">${download.price.toFixed(2)}</span>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <div className="mb-2 flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-[#D4AF37] uppercase tracking-wider">
                        {download.category}
                      </span>
                      {download.book_category && (
                        <>
                          <span className="text-[#737373]">•</span>
                          <span className="text-xs text-[#A3A3A3] uppercase tracking-wider">
                            {download.book_category.replace('_', ' ')}
                          </span>
                        </>
                      )}
                    </div>
                    
                    <h3 className="font-['Bebas_Neue'] text-2xl mb-2 group-hover:text-[#D4AF37] transition-colors">
                      {download.title}
                    </h3>
                    
                    {download.description && (
                      <p className="text-sm text-[#A3A3A3] mb-4 line-clamp-2">
                        {download.description}
                      </p>
                    )}

                    {/* Stats */}
                    <div className="flex items-center justify-between text-xs text-[#737373] mb-4">
                      <span>{download.download_count} downloads</span>
                      {download.file_size_mb && (
                        <span>{download.file_size_mb.toFixed(1)} MB</span>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="space-y-2">
                      {/* Free Download */}
                      {isFree && (
                        <button
                          onClick={() => handleDownloadPDF(download)}
                          className="w-full py-3 bg-[#D4AF37] text-black font-medium flex items-center justify-center gap-2 hover:bg-[#E5C048] transition-colors"
                        >
                          <Download size={18} />
                          DOWNLOAD FREE
                        </button>
                      )}

                      {/* Purchase Button — Shopify Buy Button preferred when
                          admin has mapped this book to a Shopify product;
                          falls back to the legacy Stripe purchase flow. */}
                      {!isFree && !isPurchased && download.shopify_product_id ? (
                        <BookBuyButton book={download} />
                      ) : !isFree && !isPurchased ? (
                        <button
                          onClick={() => handlePurchase(download)}
                          className="w-full py-3 bg-[#D4AF37] text-black font-medium flex items-center justify-center gap-2 hover:bg-[#E5C048] transition-colors"
                        >
                          <ShoppingCart size={18} />
                          PURCHASE ${download.price.toFixed(2)}
                        </button>
                      ) : null}

                      {/* Download PDF (Tier 2+ only) */}
                      {isPurchased && canDownloadPDF && (
                        <button
                          onClick={() => handleDownloadPDF(download)}
                          className="w-full py-3 bg-green-600 text-white font-medium flex items-center justify-center gap-2 hover:bg-green-700 transition-colors"
                        >
                          <Download size={18} />
                          DOWNLOAD PDF
                        </button>
                      )}

                      {/* Listen Button (Tier 2+ only) */}
                      {isPurchased && canPlayAudio && (
                        <button
                          onClick={() => handlePlayAudio(download)}
                          className="w-full py-3 bg-purple-600 text-white font-medium flex items-center justify-center gap-2 hover:bg-purple-700 transition-colors"
                        >
                          <Play size={18} />
                          LISTEN (AI AUDIOBOOK)
                        </button>
                      )}

                      {/* Locked Message for Tier 1 / Free Users */}
                      {isPurchased && !canDownloadPDF && !canPlayAudio && (
                        <div className="w-full py-3 bg-white/5 text-[#A3A3A3] font-medium flex items-center justify-center gap-2">
                          <Lock size={18} />
                          Upgrade to Family or Legacy for PDF & Audio
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Audio Player Modal */}
      {playingAudio && (
        <AudioPlayer
          audioUrl={playingAudio.audioUrl}
          title={playingAudio.title}
          fullText={playingAudio.fullText}
          onClose={() => setPlayingAudio(null)}
        />
      )}
    </div>
  );
};

export default DownloadsPage;
